import React, { useState, useEffect } from 'react';

function AdminPage({ contract, account }) {
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Check if current user is admin/owner
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Add to your component's state
  const [arweaveUrls, setArweaveUrls] = useState({});
  
  useEffect(() => {
    const checkIfAdmin = async () => {
      if (!contract || !account) return;
      
      try {
        const contractOwner = await contract.owner();
        setIsAdmin(account.toLowerCase() === contractOwner.toLowerCase());
        
        if (account.toLowerCase() === contractOwner.toLowerCase()) {
          fetchPendingSubmissions();
        }
      } catch (err) {
        console.error("Error checking admin status:", err);
      }
    };
    
    checkIfAdmin();
  }, [contract, account]);
  
  const fetchPendingSubmissions = async () => {
    try {
      setIsLoading(true);
      // Get all SubmissionReceived events
      const events = await contract.queryFilter('SubmissionReceived');
      // GROUP events by position and get the LATEST event for each position
      const latestEventsByPosition = {};
      for (const event of events) {
        const position = event.args.startPosition.toString();
        // If we haven't seen this position before, or this event is newer
        if (!latestEventsByPosition[position] || 
            event.blockNumber > latestEventsByPosition[position].blockNumber ||
            (event.blockNumber === latestEventsByPosition[position].blockNumber && 
             event.logIndex > latestEventsByPosition[position].logIndex)) {
          latestEventsByPosition[position] = event;
        }
      }
      console.log("Latest events by position:", Object.keys(latestEventsByPosition));
      const submissions = [];
      // Now check only the LATEST submission for each position
      for (const [position, event] of Object.entries(latestEventsByPosition)) {
        try {
          // CHECK CONTRACT STATE DIRECTLY - this is the source of truth
          const submitter = await contract.submitters(position);
          // If position has a submitter (not zero address), it's still pending
          if (submitter !== '0x0000000000000000000000000000000000000000') {
            console.log(`Position ${position} is pending - using latest event from block ${event.blockNumber}`);
            // Get the pending data
            const pendingURI = await contract.pendingMetadata(position);
            const size = await contract.pendingSize(position);
            // Parse metadata from the URI
            let metadata = {};
            if (pendingURI.startsWith('data:application/json;base64,')) {
              try {
                const json = atob(pendingURI.split(',')[1]);
                metadata = JSON.parse(json);
              } catch (err) {
                console.error(`Error parsing metadata for position ${position}:`, err);
              }
            }
            submissions.push({
              position,
              size: size.toString(),
              submitter,
              metadata,
              blockNumber: event.blockNumber,
              logIndex: event.logIndex
            });
          } else {
            console.log(`Position ${position} has no submitter - skipping`);
          }
        } catch (err) {
          // Position not pending, skip
          console.log(`Position ${position} not pending:`, err.message);
        }
      }
      // Sort by block number (newest first) for better UX
      submissions.sort((a, b) => {
        if (b.blockNumber !== a.blockNumber) {
          return b.blockNumber - a.blockNumber;
        }
        return b.logIndex - a.logIndex;
      });
      console.log("Final submissions:", submissions.length);
      setPendingSubmissions(submissions);
      setIsLoading(false);
    } catch (err) {
      console.error("Error fetching pending submissions:", err);
      setError("Error: " + err.message);
      setIsLoading(false);
    }
  };
  
  // Add this function to update an Arweave URL
  const handleArweaveUrlChange = (position, url) => {
    setArweaveUrls({
      ...arweaveUrls,
      [position]: url
    });
  };
  
  // Add this function to update metadata before approval
  const updateMetadataWithArweave = async (position) => {
    try {
      const arweaveUrl = arweaveUrls[position];
      if (!arweaveUrl) {
        return false; // No Arweave URL provided
      }
      
      // Get current metadata
      const pendingURI = await contract.pendingMetadata(position);
      
      // Parse metadata
      if (pendingURI.startsWith('data:application/json;base64,')) {
        const json = atob(pendingURI.split(',')[1]);
        const metadata = JSON.parse(json);
        
        // Update image URL with Arweave URL
        metadata.image = arweaveUrl;
        
        // Create updated metadata URI
        const updatedURI = `data:application/json;base64,${btoa(JSON.stringify(metadata))}`;
        
        // Update on contract
        const tx = await contract.updatePendingMetadata(position, updatedURI);
        await tx.wait();
        return true;
      }
      return false;
    } catch (err) {
      console.error("Error updating metadata:", err);
      setError("Metadata update error: " + err.message);
      return false;
    }
  };
  
  // Modify your existing handleApprove function
  const handleApprove = async (position) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      
      // Update metadata with Arweave URL if provided
      const arweaveUrl = arweaveUrls[position];
      if (arweaveUrl) {
        await updateMetadataWithArweave(position);
      }
      
      // Proceed with approval
      const tx = await contract.approvePosition(position);
      await tx.wait();
      
      setSuccess(`Position #${position} approved successfully with permanent storage!`);
      fetchPendingSubmissions();
    } catch (err) {
      console.error("Error approving position:", err);
      setError("Error: " + err.message);
      setIsLoading(false);
    }
  };
  
  const handleReject = async (position) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      const tx = await contract.rejectPosition(position);
      await tx.wait();
      setSuccess(`Position #${position} rejected and refunded successfully!`);
      // Refresh both admin submissions and main app pending pixels
      fetchPendingSubmissions();
      // IMPORTANT: Also refresh the main app's pending pixels
      if (typeof window.refreshPendingPixels === 'function') {
        window.refreshPendingPixels();
      }
    } catch (err) {
      console.error("Error rejecting position:", err);
      setError("Error: " + err.message);
      setIsLoading(false);
    }
  };
  
  // Show access denied if not admin
  if (!isAdmin) {
    return (
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ color: '#C62828', marginBottom: '20px' }}>Access Denied</h2>
        <p>You do not have permission to access the admin page.</p>
      </div>
    );
  }
  
  return (
    <div style={{
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '20px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      <h2 style={{ color: '#2E7D32', marginBottom: '20px' }}>Admin: Pending Submissions</h2>
      
      {error && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#FFEBEE',
          color: '#C62828',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}
      
      {success && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#E8F5E9',
          color: '#2E7D32',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {success}
        </div>
      )}
      
      <button 
        onClick={fetchPendingSubmissions}
        style={{ 
          padding: '10px 20px',
          backgroundColor: '#2196F3',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          marginBottom: '20px'
        }}
      >
        Refresh Submissions
      </button>
      {/* Force Refresh All button */}
      <button 
        onClick={() => {
          fetchPendingSubmissions();
          if (typeof window.checkPendingPixels === 'function') {
            window.checkPendingPixels();
          }
        }}
        style={{ 
          padding: '10px 20px',
          backgroundColor: '#4CAF50',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          marginBottom: '20px',
          marginLeft: '10px'
        }}
      >
        Force Refresh All
      </button>
      
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading submissions...
        </div>
      ) : pendingSubmissions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          No pending submissions found
        </div>
      ) : (
        <div>
          {pendingSubmissions.map((submission, index) => (
            <div key={index} style={{
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '20px',
              backgroundColor: '#f9f9f9'
            }}>
              <div style={{ display: 'flex', marginBottom: '15px' }}>
                <div style={{ flex: '1' }}>
                  <h3 style={{ margin: '0 0 10px 0' }}>Position #{submission.position}</h3>
                  <p><strong>Submitter:</strong> {submission.submitter}</p>
                  <p><strong>Size:</strong> {submission.size}×{submission.size}</p>
                  <p><strong>Website:</strong> {submission.metadata.website || 'None'}</p>
                  <p><strong>Description:</strong> {submission.metadata.description || 'None'}</p>
                  <p>
                    <strong>Arweave URL:</strong>
                    <input 
                      type="text" 
                      value={arweaveUrls[submission.position] || ''} 
                      onChange={(e) => handleArweaveUrlChange(submission.position, e.target.value)} 
                      style={{ marginLeft: '10px', padding: '5px', width: '70%' }}
                    />
                  </p>
                  <div style={{ marginBottom: '15px' }}>
                    <label><strong>Arweave URL:</strong></label>
                    <input 
                      type="text" 
                      placeholder="https://arweave.net/your-transaction-id"
                      value={arweaveUrls[submission.position] || ''}
                      onChange={(e) => handleArweaveUrlChange(submission.position, e.target.value)}
                      style={{ 
                        width: '100%', 
                        padding: '8px',
                        marginTop: '5px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                    <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                      Upload image to Arweave first, then paste URL here before approving
                    </small>
                  </div>
                </div>
                <div style={{ marginLeft: '20px', width: '200px' }}>
                  {submission.metadata.image && (
                    <img 
                      src={submission.metadata.image} 
                      alt="Submission preview" 
                      style={{ 
                        width: '100%', 
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }} 
                    />
                  )}
                </div>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button 
                  onClick={() => handleReject(submission.position)}
                  style={{ 
                    padding: '10px 20px',
                    backgroundColor: '#F44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Reject
                </button>
                <button 
                  onClick={() => handleApprove(submission.position)}
                  style={{ 
                    padding: '10px 20px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Approve
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminPage;